"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { GROQButton } from "@/sanity/lib/definitions";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { ICourseCover } from "@/sanity/pages/PageCourseGalleryTemplate";
import { PipeChip } from "./_shared";
import heroArc from "@/public/courseGallery/hero-arc.svg";

gsap.registerPlugin(ScrollTrigger);

interface HeroSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  subtitle?: PortableTextBlock[];
  cta?: GROQButton;
  cards?: ICourseCover[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Wheel geometry, derived from the Figma hero (3780:19213).
//
// Least-squares circle fit through the seven cover centres in the static Figma
// frame lands on radius 3628px, centred 3878px below the top of the cards band
// (residuals 4–15px). So the design's fan really is a wheel — and at that radius
// Figma's 405px cover pitch works out to a 6.40° slice, which is exactly the
// ~6° tilt step the frame uses. Rotating that circle reproduces the design, and
// the wheel then drifts on its own with scrolling adding a temporary boost.
//
// The ticked arc behind the covers (3780:19214) is a *different*, tighter circle
// — radius 2084.5px centred 2277px down — which is why it cuts across each card
// a couple of hundred px below its top edge. It stays static.
//
// Values are px at the design width; radius is converted through the live root
// font-size so it tracks the fluid rem scale (1rem = 8px at the 1920 design).
// ─────────────────────────────────────────────────────────────────────────────
const CARD_W = 323.491;
const CARD_H = 484;
const CARD_RADIUS = 3628; // covers ride this circle
const WHEEL_CENTRE_Y = 3878; // below the top of the cards band
const ARC_RADIUS = 2084.5; // decorative tick arc — separate, tighter circle
const ARC_CENTRE_Y = 2277;
const BAND_H = 628; // visible slice of the wheel
const TARGET_STEP = 405; // centre-to-centre cover pitch in Figma

/** Mobile hero (Figma 4186:7224) is the same wheel scaled uniformly: its
 *  206.72px covers over the 323.491px desktop ones, and the tick-arc width
 *  (2684.56px) is exactly the desktop arc × the same factor. The pitch this
 *  predicts (405 × 0.639 ≈ 259px) matches the mobile frame's ~254px average,
 *  so one scale factor reproduces the whole layout. */
const MOBILE_SCALE = 206.72 / 323.491;

const px = (n: number) => `${n / 8}rem`;

/** Slice that reproduces Figma's cover pitch on the fitted circle: 6.40°. */
const SLICE_RAD = 2 * Math.asin(TARGET_STEP / (2 * CARD_RADIUS));
const SLICE_DEG = SLICE_RAD * (180 / Math.PI);

/** Idle drift, in covers per second — about 100px/s at the 1920 design width. */
const AUTO_CARDS_PER_SEC = 0.25;

/** Peak speed scrolling adds, in covers per second. Signed: scrolling down adds
 *  it to the drift, scrolling up subtracts, so a decent flick upward outruns the
 *  drift and turns the wheel the other way. */
const SCROLL_BOOST_CARDS_PER_SEC = 4;

/** Scroll speed (px/s) that earns the full boost; anything faster clamps. */
const SCROLL_BOOST_FULL_AT = 2000;

/** Decay time constant of the boost — how quickly the wheel eases back down to
 *  its idle drift once scrolling stops. */
const BOOST_DECAY_TAU = 0.45;

/** Entrance. The band fades up while the wheel eases in from a few covers back,
 *  so the gallery arrives already turning instead of popping into place. The
 *  spin runs in the drift direction, which is what makes it hand over to the
 *  idle drift without a seam. Delay lines the two up behind the hero copy. */
const INTRO_SPIN_CARDS = 2.5;
const INTRO_DURATION = 1.6;
const INTRO_DELAY = 0.3;

function CoverCard({
  cover,
  className = "",
  style,
}: {
  cover: ICourseCover;
  className?: string;
  style?: React.CSSProperties;
}) {
  // No `position` class here on purpose — the caller supplies it. Adding
  // `relative` would beat an appended `absolute` (Tailwind emits `.relative`
  // after `.absolute`), which would drop the cards back into normal flow.
  return (
    <div
      className={`overflow-hidden border border-white/10 bg-white/[0.04] rounded-[1.5rem] ${className}`}
      style={style}>
      {cover.image && (
        <Image
          src={cover.image.src}
          alt={cover.name ?? ""}
          fill
          quality={100}
          sizes="(max-width: 992px) 60vw, 40.5rem"
          placeholder={cover.image.lqip ? "blur" : undefined}
          blurDataURL={cover.image.lqip}
          className="object-cover"
        />
      )}
    </div>
  );
}

/** The wheel band, at a given uniform scale (1 = desktop design, MOBILE_SCALE
 *  = the 435 mobile frame). Geometry, drift and scroll boost are identical —
 *  the slice angle is scale-invariant, so the two share slot count and speed. */
function WheelBand({ covers, slots, scale }: { covers: ICourseCover[]; slots: number; scale: number }) {
  const s = (n: number) => px(n * scale);

  const bandRef = useRef<HTMLDivElement>(null);
  const wheelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wheel = wheelRef.current;
    const band = bandRef.current;
    if (!wheel || !band || slots === 0) return;

    const items = Array.from(wheel.children) as HTMLElement[];
    if (items.length === 0) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Lay the covers around the circle — same math as the GSAP wheel recipe,
    // but measured from the wheel's own origin (it is a zero-size anchor at the
    // circle centre) and scaled through the live rem so it tracks the fluid
    // root font-size instead of assuming 1rem = 8px.
    let radiusPx = 0; // live wheel radius, kept fresh by layout() for the drag math

    const layout = () => {
      const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const radius = ((CARD_RADIUS * scale) / 8) * remPx;
      radiusPx = radius;
      const slice = (2 * Math.PI) / items.length;

      items.forEach((item, i) => {
        const angle = i * slice;
        gsap.set(item, {
          rotation: `${angle}_rad`,
          xPercent: -50,
          yPercent: -50,
          x: radius * Math.sin(angle),
          y: -radius * Math.cos(angle),
        });
      });
    };

    layout();
    window.addEventListener("resize", layout);

    // Rotation is integrated per frame rather than scrubbed: the wheel always
    // drifts at AUTO_CARDS_PER_SEC, and scrolling injects a boost on top that
    // decays back to that drift. A full turn is an identical arrangement, so
    // wrapping the angle at 360° is seamless.
    let trigger: ScrollTrigger | undefined;
    let tick: ((time: number, deltaMs: number) => void) | undefined;
    let intro: gsap.core.Tween | undefined;
    let removeDrag: (() => void) | undefined;

    if (!reduce) {
      const baseSpeed = AUTO_CARDS_PER_SEC * SLICE_DEG; // deg/s, always positive
      const maxBoost = SCROLL_BOOST_CARDS_PER_SEC * SLICE_DEG; // deg/s
      let rotation = 0;
      let boost = 0; // signed extra deg/s, decays to 0
      let visible = false;

      // Entrance offset, eased to zero on top of whatever the drift is doing —
      // the two are simply added, so the hand-over needs no coordination.
      const entrance = { offset: INTRO_SPIN_CARDS * SLICE_DEG };
      intro = gsap.to(entrance, {
        offset: 0,
        duration: INTRO_DURATION,
        delay: INTRO_DELAY,
        ease: "power3.out",
      });

      trigger = ScrollTrigger.create({
        trigger: band,
        start: "top bottom",
        end: "bottom top",
        onToggle: (self) => {
          visible = self.isActive;
        },
        onUpdate: (self) => {
          // Signed, so the wheel follows the scroll: down pushes it along its
          // drift, up drags it back the other way. A new reading wins whenever
          // it is stronger or points the other way; otherwise the decay owns it.
          const reach = gsap.utils.clamp(-1, 1, self.getVelocity() / SCROLL_BOOST_FULL_AT);
          const target = reach * maxBoost;
          if (Math.abs(target) > Math.abs(boost) || Math.sign(target) !== Math.sign(boost)) boost = target;
        },
      });
      visible = trigger.isActive; // onToggle may not fire if already in view

      // Dragging: dx along the band is arc length on the wheel, so dividing by
      // the live radius gives the rotation delta — the covers track the finger
      // exactly. While a pointer is down the auto-drift pauses; on release the
      // measured angular velocity is handed to the boost, so a throw coasts
      // and decays back into the idle drift through the machinery that already
      // does that for scrolling. Engages past a small threshold so stray taps
      // don't twitch the wheel.
      let dragId: number | null = null;
      let draggingWheel = false;
      let dragStartX = 0;
      let dragLastX = 0;
      let dragLastT = 0;
      let dragVel = 0; // deg/s, signed as applied to `rotation`

      const onDown = (e: PointerEvent) => {
        if (e.pointerType === "mouse" && e.button !== 0) return;
        dragId = e.pointerId;
        draggingWheel = false;
        dragStartX = dragLastX = e.clientX;
        dragLastT = e.timeStamp;
        dragVel = 0;
      };
      const onMove = (e: PointerEvent) => {
        if (dragId === null || e.pointerId !== dragId || radiusPx === 0) return;
        if (!draggingWheel) {
          if (Math.abs(e.clientX - dragStartX) <= 6) return;
          draggingWheel = true;
          band.setPointerCapture(dragId);
          dragLastX = e.clientX;
          dragLastT = e.timeStamp;
          return;
        }
        const dx = e.clientX - dragLastX;
        const dt = Math.max(1, e.timeStamp - dragLastT) / 1000;
        dragLastX = e.clientX;
        dragLastT = e.timeStamp;
        const dRot = (dx / radiusPx) * (180 / Math.PI);
        rotation = (rotation + dRot) % 360;
        dragVel = dRot / dt;
      };
      const onUp = (e: PointerEvent) => {
        if (dragId === null || e.pointerId !== dragId) return;
        dragId = null;
        if (!draggingWheel) return;
        draggingWheel = false;
        // The tick moves at -(baseSpeed + boost) deg/s; matching the release
        // velocity means boost = -dragVel - baseSpeed, clamped like scroll.
        boost = gsap.utils.clamp(-maxBoost, maxBoost, -dragVel - baseSpeed);
      };
      const onDragStart = (e: Event) => e.preventDefault();

      band.addEventListener("pointerdown", onDown);
      band.addEventListener("pointermove", onMove);
      band.addEventListener("pointerup", onUp);
      band.addEventListener("pointercancel", onUp);
      band.addEventListener("dragstart", onDragStart);
      removeDrag = () => {
        band.removeEventListener("pointerdown", onDown);
        band.removeEventListener("pointermove", onMove);
        band.removeEventListener("pointerup", onUp);
        band.removeEventListener("pointercancel", onUp);
        band.removeEventListener("dragstart", onDragStart);
      };

      tick = (_time, deltaMs) => {
        // offsetParent is null while the band's breakpoint wrapper is
        // display:none — the other-breakpoint instance idles for free.
        if (!visible || !band.offsetParent) return;
        const dt = Math.min(deltaMs, 100) / 1000; // clamp background-tab jumps
        if (!draggingWheel) {
          rotation = (rotation - (baseSpeed + boost) * dt) % 360;
          boost *= Math.exp(-dt / BOOST_DECAY_TAU);
        }
        gsap.set(wheel, { rotation: rotation + entrance.offset });
      };
      gsap.ticker.add(tick);
    }

    return () => {
      window.removeEventListener("resize", layout);
      if (tick) gsap.ticker.remove(tick);
      removeDrag?.();
      trigger?.kill();
      intro?.kill();
    };
  }, [slots, scale]);

  return (
    <div
      ref={bandRef}
      className="relative w-full cursor-grab select-none [touch-action:pan-y] active:cursor-grabbing"
      style={{ height: s(BAND_H) }}>
      {/* Static tick arc: its bottom edge sits on the wheel centre. */}
      <Image
        src={heroArc}
        alt=""
        aria-hidden
        priority
        className="pointer-events-none absolute left-1/2 h-auto max-w-none -translate-x-1/2 -translate-y-full"
        style={{ top: s(ARC_CENTRE_Y), width: s(ARC_RADIUS * 2 + 32) }}
      />

      {/* Zero-size anchor at the circle centre; GSAP rotates the child about
          its own origin, which is that same point. */}
      <div
        className="absolute left-1/2 h-0 w-0"
        style={{ top: s(WHEEL_CENTRE_Y) }}>
        <div
          ref={wheelRef}
          className="h-0 w-0">
          {Array.from({ length: slots }, (_, i) => (
            <CoverCard
              key={i}
              cover={covers[i % covers.length]}
              className="absolute top-0 left-0"
              style={{ width: s(CARD_W), height: s(CARD_H) }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HeroSection({ chip, title, subtitle, cta, cards }: HeroSectionProps) {
  const covers = cards ?? [];

  // Close the circle at Figma's pitch so the wheel never rotates a gap into
  // view, rounded to a whole number of cover repeats (7 covers → 56 slots) so
  // the pattern — and therefore the rotation — is seamless.
  const slots = covers.length ? covers.length * Math.max(1, Math.round((2 * Math.PI) / SLICE_RAD / covers.length)) : 0;

  return (
    <section className="relative z-[5] w-full overflow-x-clip bg-black pt-[6rem] lg:pt-[12.5rem]">
      <div className="flex w-full flex-col items-center gap-[5rem] lg:gap-[7rem]">
        {/* ── Copy ─────────────────────────────────────────────────────── */}
        <div className="flex w-full flex-col items-center gap-[3.5rem] px-[2rem] lg:gap-[4rem] lg:px-[45rem]">
          <div className="flex flex-col items-center gap-[2.5rem] text-center lg:gap-[3rem]">
            {chip && (
              <FadeIn
                delay={0}
                immediate>
                <PipeChip>{chip}</PipeChip>
              </FadeIn>
            )}

            <div className="flex flex-col items-center gap-[1rem]">
              {title && (
                <FadeIn
                  delay={0.1}
                  immediate
                  className="richtext richtext-center max-w-full [text-wrap:balance] lg:w-[171.625rem] prose-p:m-0 prose-p:font-news prose-p:text-[4rem] prose-p:leading-[1.1] prose-p:tracking-[-0.08rem] prose-p:text-white lg:prose-p:text-[7.5rem] lg:prose-p:tracking-[-0.15rem] prose-em:font-news prose-em:italic prose-headings:m-0 prose-headings:font-news prose-headings:text-[5rem] prose-headings:leading-[1.1] prose-headings:tracking-[-0.08rem] prose-headings:text-white lg:prose-headings:text-[7.5rem] lg:prose-headings:tracking-[-0.15rem]">
                  <PortableText
                    value={title}
                    components={richTextComponent}
                  />
                </FadeIn>
              )}

              {subtitle && (
                <FadeIn
                  delay={0.15}
                  immediate
                  className="richtext richtext-center max-w-full opacity-60 lg:w-[78.125rem] prose-p:m-0 prose-p:font-geist prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:tracking-[0.04rem] prose-p:text-white lg:prose-p:text-[2.25rem] lg:prose-p:tracking-[0.045rem]">
                  <PortableText
                    value={subtitle}
                    components={richTextComponent}
                  />
                </FadeIn>
              )}
            </div>
          </div>

          <FadeIn
            delay={0.2}
            immediate
            className="w-full lg:w-fit">
            <CTAButton data={cta ?? {}} />
          </FadeIn>
        </div>

        {/* ── Cover wheel ──────────────────────────────────────────────── */}
        {covers.length > 0 && (
          <>
            {/* Desktop — circular wheel. The fade-up rides on the wrapper so
                it stays clear of the transforms GSAP owns inside the band. */}
            <FadeIn
              delay={INTRO_DELAY}
              duration={INTRO_DURATION * 0.6}
              y={48}
              immediate
              className="hidden w-full lg:block">
              <WheelBand
                covers={covers}
                slots={slots}
                scale={1}
              />
            </FadeIn>

            {/* Mobile — the same wheel at the Figma mobile frame's scale
                (4186:7224). */}
            <FadeIn
              delay={INTRO_DELAY}
              duration={INTRO_DURATION * 0.6}
              y={32}
              immediate
              className="w-full lg:hidden">
              <WheelBand
                covers={covers}
                slots={slots}
                scale={MOBILE_SCALE}
              />
            </FadeIn>
          </>
        )}
      </div>
    </section>
  );
}
